import type {
    AbstractAnalyticsDispatcher,
    OverridableActionsTypes,
    OverridableBusinessEventTypes,
} from "@asurion-core/eventualize";
import type { CallCategory } from "@expert/shared-types";

import { getLogger } from "@expert/logging";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import type { GuideOption, GuideSelectionObject, UiMetadata } from "../logic-builder";

import { OpenGuideSource } from "../types";
import { defaultUiMetadata } from "./defaults";

const logger = getLogger({
    module: "useGuideStore",
});

export interface SessionMetadata {
    [key: string]: unknown;
}

export interface AnalyticsInfo {
    currentStepId: string;
    completedSteps: string;
    completedStepCount: number;
}

export interface MonitoringProps {
    dispatcher: AbstractAnalyticsDispatcher<OverridableBusinessEventTypes, OverridableActionsTypes>;
}

export interface GuideStore {
    availableGuides: GuideSelectionObject[];
    setAvailableGuides: (availableGuides?: GuideSelectionObject[]) => void;

    currentGuideName?: string;
    setCurrentGuideName: ({ dispatcher, currentGuideName }: MonitoringProps & { currentGuideName?: string }) => void;

    analyticsInfo: AnalyticsInfo;
    setAnalyticsInfo: (analyticsInfo: AnalyticsInfo) => void;

    disableAutomaticOpen: boolean;
    setDisableAutomaticOpen: (value: boolean) => void;

    errorStateSessionId?: string;
    setErrorStateSessionId: (sessionId?: string) => void;
    isErrorState: boolean;
    setIsErrorState: (value: boolean) => void;

    isLoadingSimplrSession: boolean;
    setIsLoadingSimplrSession: (isLoading: boolean) => void;

    isWaitingForNextStep: boolean;
    setIsWaitingForNextStep: (isWaiting: boolean) => void;

    openGuideSource?: OpenGuideSource;
    setOpenGuideSource: (openGuideSource?: OpenGuideSource) => void;

    sessionMetadata: SessionMetadata;
    setSessionMetadata: (sessionMetadata: SessionMetadata) => void;

    uiMetadata: UiMetadata;
    setUiMetadata: (uiMetadata?: UiMetadata) => void;

    selectedOptions: Record<string, GuideOption>;
    setSelectedOptions: (selectedOptions: Record<string, GuideOption>) => void;

    recommendation?: CallCategory;
    setRecommendation: (recommendation?: string) => void;

    resetGuide: () => void;
}

export const useGuideStore = create<GuideStore>()(
    persist(
        immer(
            devtools(
                (set) => ({
                    availableGuides: [],
                    setAvailableGuides: (availableGuides: GuideSelectionObject[] = []) => {
                        set(
                            (state) => {
                                state.availableGuides = availableGuides;
                            },
                            false,
                            "setAvailableGuides",
                        );
                    },
                    currentGuideName: undefined,
                    setCurrentGuideName: ({
                        dispatcher,
                        currentGuideName,
                    }: MonitoringProps & { currentGuideName?: string }) => {
                        set(
                            (state) => {
                                if (state.currentGuideName && currentGuideName) {
                                    // there is already a guide selected (a guide in progress)
                                    logger.info(
                                        { availableGuides: state.availableGuides, guide: currentGuideName },
                                        "Solve Guide | open new guided flow within in progress guided flow",
                                    );
                                    void dispatcher.dispatchBusinessEvent("Guide_OpenGuide", {
                                        guide: currentGuideName,
                                        source: "manual",
                                        sourceDetails: "in-progress-guide",
                                    });
                                } else if (currentGuideName) {
                                    // a guided flow is being selected and started
                                    logger.info(
                                        { availableGuides: state.availableGuides, guide: currentGuideName },
                                        "Solve Guide | open new guided flow",
                                    );
                                    void dispatcher.dispatchBusinessEvent("Guide_OpenGuide", {
                                        guide: currentGuideName,
                                        ...state.openGuideSource,
                                    });
                                }

                                logger.info({ currentGuideName }, `Solve Guide | Setting currentGuideName`);
                                state.currentGuideName = currentGuideName;
                            },
                            false,
                            "setCurrentGuideName",
                        );
                    },
                    analyticsInfo: {
                        currentStepId: "",
                        completedSteps: "",
                        completedStepCount: 0,
                    },
                    setAnalyticsInfo: (analyticsInfo: AnalyticsInfo) => {
                        set(
                            (state) => {
                                state.analyticsInfo = analyticsInfo;
                            },
                            false,
                            "setAnalyticsInfo",
                        );
                    },
                    disableAutomaticOpen: false,
                    setDisableAutomaticOpen: (value: boolean) => {
                        set(
                            (state) => {
                                state.disableAutomaticOpen = value;
                            },
                            false,
                            "setDisableAutomaticOpen",
                        );
                    },
                    errorStateSessionId: undefined,
                    setErrorStateSessionId: (sessionId?: string) => {
                        set(
                            (state) => {
                                if (!sessionId) {
                                    state.errorStateSessionId = undefined;
                                } else if (state.errorStateSessionId) {
                                    state.errorStateSessionId = `${state.errorStateSessionId}_RESTARTED`;
                                } else if (sessionId) {
                                    state.errorStateSessionId = `${sessionId}_RESTARTED`;
                                }
                            },
                            false,
                            "setErrorStateSessionId",
                        );
                    },
                    isErrorState: false,
                    setIsErrorState: (value: boolean) => {
                        set(
                            (state) => {
                                state.isErrorState = value;
                            },
                            false,
                            "setIsErrorState",
                        );
                    },
                    isLoadingSimplrSession: false,
                    setIsLoadingSimplrSession: (isLoading: boolean) => {
                        set(
                            (state) => {
                                state.isLoadingSimplrSession = isLoading;
                            },
                            false,
                            "setIsLoadingSimplrSession",
                        );
                    },
                    isWaitingForNextStep: false,
                    setIsWaitingForNextStep: (isWaiting: boolean) => {
                        set(
                            (state) => {
                                state.isWaitingForNextStep = isWaiting;
                            },
                            false,
                            "setIsWaitingForNextStep",
                        );
                    },
                    openGuideSource: undefined,
                    setOpenGuideSource: (openGuideSource?: OpenGuideSource) => {
                        set(
                            (state) => {
                                state.openGuideSource = openGuideSource;
                            },
                            false,
                            "setOpenGuideSource",
                        );
                    },
                    sessionMetadata: {},
                    setSessionMetadata: (sessionMetadata: SessionMetadata) => {
                        set(
                            (state) => {
                                state.sessionMetadata = sessionMetadata;
                            },
                            false,
                            "setSessionMetadata",
                        );
                    },
                    selectedOptions: {},
                    setSelectedOptions: (selectedOptions: Record<string, GuideOption>) => {
                        set(
                            (state) => {
                                state.selectedOptions = selectedOptions;
                            },
                            false,
                            "setSelectedOptions",
                        );
                    },
                    uiMetadata: defaultUiMetadata,
                    setUiMetadata: (uiMetadata?: UiMetadata) => {
                        set(
                            (state) => {
                                state.uiMetadata = uiMetadata || defaultUiMetadata;
                            },
                            false,
                            "setUiMetadata",
                        );
                    },
                    recommendation: undefined,
                    setRecommendation: (recommendation?: CallCategory) => {
                        set(
                            (state) => {
                                state.recommendation = recommendation;
                            },
                            false,
                            "setRecommendation",
                        );
                    },
                    resetGuide: () => {
                        set(
                            (state) => {
                                logger.info("Solve Guide | Resetting guide state");
                                state.analyticsInfo = {
                                    currentStepId: "",
                                    completedSteps: "",
                                    completedStepCount: 0,
                                };
                                // Clear so a new session can't auto-open against a prior session's
                                // guides or recommendation before its Simplr stream has loaded.
                                state.availableGuides = [];
                                state.recommendation = undefined;
                                state.currentGuideName = undefined;
                                state.disableAutomaticOpen = false;
                                state.isErrorState = false;
                                state.isLoadingSimplrSession = true;
                                state.isWaitingForNextStep = false;
                                state.errorStateSessionId = undefined;
                                state.openGuideSource = undefined;
                                state.selectedOptions = {};
                                state.sessionMetadata = {};
                                state.uiMetadata = defaultUiMetadata;
                            },
                            false,
                            "resetGuide",
                        );
                    },
                }),
                {
                    enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                    store: "simplr-guide-store",
                    name: "simplr-guide-store",
                },
            ),
        ),
        { name: "guide-store", skipHydration: true },
    ),
);
